import axios from "axios";
import { ENDPOINT } from "constants/environment";
import { encryptData } from "helpers";
import { decryptData } from "helpers";
import { getHeaderConfig } from "helpers";
import { consoleLog } from "helpers";

export const getPromptFragmentsAPI = async (
  { pageIndex, skip },
  isLoading,
  accessJWT,
  args,
  fragmentId = ""
) => {
  try {
    if (isLoading) isLoading(true);
    const config = getHeaderConfig(null, accessJWT);

    let response;
    if (fragmentId) {
      const body = { id: fragmentId };
      response = await axios.post(
        `${ENDPOINT}/app/v1/promptFragment`,
        encryptData(body),
        config
      );
    } else {
      const queryBody = {
        skip: pageIndex + 1,
        limit: skip,
      };
      response = await axios.post(
        `${ENDPOINT}/app/v1/promptFragment/filter`,
        encryptData(queryBody),
        config
      );
    }

    const parsedResponseData = JSON.parse(decryptData(response?.data));

    if (parsedResponseData && parsedResponseData.statusCode === 200) {
      if (isLoading) isLoading(false);
      return parsedResponseData?.data;
    }
    isLoading(false);
    throw Error;
  } catch (error) {
    isLoading(false);
    console.error("Error fetching data:", error);
    return null;
  }
};

export const getFilterPromptFragmentsAPI = async (
  { pageIndex, limit },
  payload,
  setIsLoading,
  accessJWT
) => {
  try {
    setIsLoading(true);
    const config = getHeaderConfig(null, accessJWT);

    const body = {
      ...payload,
      skip: pageIndex + 1,
      limit: limit,
    };

    const response = await axios.post(
      `${ENDPOINT}/app/v1/promptFragment/filter`,
      encryptData(body),
      config
    );

    const parsedResponseData = JSON.parse(decryptData(response?.data));

    if (parsedResponseData?.statusCode === 200) {
      if (setIsLoading) setIsLoading(false);
      return {
        data: parsedResponseData?.data.data,
        totalPages: parsedResponseData.data.pagination?.totalPages,
      };
    }

    throw new Error("Error fetching data");
  } catch (error) {
    console.error("Error fetching data:", error);
    return { data: [], totalPages: 0 };
  } finally {
    setIsLoading(false);
  }
};

export const createOrUpdatePromptFragmentsAPI = async (
  args,
  isLoading,
  accessJWT
) => {
  const API_URL = `${ENDPOINT}/app/v1/promptFragment`;
  try {
    if (isLoading) isLoading(true);
    const config = getHeaderConfig(null, accessJWT);
    const response = args?.id
      ? await axios.put(API_URL, encryptData(args), config)
      : await axios.post(API_URL, encryptData(args), config);
    const parsedResponseData = JSON.parse(decryptData(response?.data));

    if (parsedResponseData?.statusCode === 200) {
      if (isLoading) isLoading(false);
      return { success: true, data: parsedResponseData?.data };
    }

    if (isLoading) isLoading(false);
    return {
      success: false,
      message: parsedResponseData?.message || "Unknown error",
    };
  } catch (error) {
    if (isLoading) isLoading(false);

    try {
      // Extract response if encrypted error exists
      const encryptedErrorData = error?.response?.data;
      const parsedError = encryptedErrorData
        ? JSON.parse(decryptData(encryptedErrorData))
        : null;

      return {
        success: false,
        message: parsedError?.message || "An unexpected error occurred.",
      };
    } catch (innerErr) {
      console.error("Error parsing response:", innerErr);
    }

    console.error("Error creating/updating prompt fragment:", error);
    return {
      success: false,
      message: "Error creating/updating prompt fragment.",
    };
  }
};

export const deletePromptFragmentAPI = async (args, isLoading, accessJWT) => {
  const API_URL = `${ENDPOINT}/app/v1/promptFragment?id=${args.id}`;
  try {
    if (isLoading) isLoading(true);
    const config = getHeaderConfig(null, accessJWT);
    const response = await axios.delete(API_URL, config);
    const parsedResponseData = JSON.parse(decryptData(response?.data));
    if (parsedResponseData) {
      if (isLoading) isLoading(false);
      return parsedResponseData;
    }
    if (isLoading) isLoading(false);
    isLoading(false);
    throw Error;
  } catch (error) {
    isLoading(false);
    console.error("Error deleting prompt:", error);
    return null;
  }
};

export const getAutoSuggestAPI = async (
  query,
  fieldType,
  isLoading,
  accessJWT
) => {
  try {
    if (isLoading) isLoading(true);

    const body = {
      key: query,
    };

    const queryBody = {
      name: query,
    };

    const url =
      fieldType === "key"
        ? `${ENDPOINT}/app/v1/promptFragment/suggestion?key=${query}`
        : `${ENDPOINT}/app/v1/promptFragment/suggestion?name=${query}`;

    const config = getHeaderConfig(null, accessJWT);

    consoleLog(["Request Headers:", config]);

    const response = await axios.post(
      url,
      fieldType === "key" ? encryptData(body) : encryptData(queryBody),
      config
    );

    const parsedResponseData = JSON.parse(decryptData(response?.data));
    consoleLog(["Parsed Response Data:", parsedResponseData]);

    if (parsedResponseData && parsedResponseData?.statusCode === 200) {
      if (isLoading) isLoading(false);
      return parsedResponseData?.data;
    }

    throw new Error(
      `Failed to fetch suggestions, Status Code: ${parsedResponseData?.statusCode}`
    );
  } catch (error) {
    console.error("Error fetching auto-suggest data:", error);

    if (error.response) {
      console.error(
        `Response Error: ${error.response.status} - ${error.response.data}`
      );
    }

    if (isLoading) isLoading(false);

    return null;
  } finally {
    if (isLoading) isLoading(false);
  }
};
